'use client';

import { useState, useEffect } from 'react';
import { inventoryService, type InventoryItem } from '@/lib/services/inventoryService';

interface InventoryBadgeProps {
  productId: string;
  inventoryMap: Record<string, InventoryItem>;
}

export function InventoryBadge({ productId, inventoryMap }: InventoryBadgeProps) {
  const item = inventoryMap?.[productId];

  if (!item) {
    return null;
  }

  const available = item.availableQuantity;
  const threshold = item.lowStockThreshold;

  if (available <= 0) {
    return (
      <span className="inline-flex items-center gap-1 bg-red-100 text-red-700 text-xs font-bold px-2.5 py-1 rounded-full">
        <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
        缺貨
      </span>
    );
  }

  if (available <= threshold) {
    return (
      <span className="inline-flex items-center gap-1 bg-yellow-100 text-yellow-700 text-xs font-bold px-2.5 py-1 rounded-full">
        <span className="w-1.5 h-1.5 rounded-full bg-yellow-500 animate-pulse" />
        僅剩{available}件
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 text-xs font-bold px-2.5 py-1 rounded-full">
      <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
      庫存充足
    </span>
  );
}

interface InventoryProviderProps {
  children: (inventoryMap: Record<string, InventoryItem>, loading: boolean) => React.ReactNode;
}

export function InventoryProvider({ children }: InventoryProviderProps) {
  const [inventoryMap, setInventoryMap] = useState<Record<string, InventoryItem>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchInventory() {
      try {
        const items = await inventoryService.getAll();
        const map: Record<string, InventoryItem> = {};
        items?.forEach((item) => {
          map[item.productId] = item;
        });
        setInventoryMap(map);
      } catch {
        // Silently fail - inventory badges just won't show
      } finally {
        setLoading(false);
      }
    }

    fetchInventory();

    // Subscribe to real-time changes
    const unsubscribe = inventoryService.subscribeToChanges((items) => {
      const map: Record<string, InventoryItem> = {};
      items?.forEach((item) => {
        map[item.productId] = item;
      });
      setInventoryMap(map);
    });

    return unsubscribe;
  }, []);

  return <>{children(inventoryMap, loading)}</>;
}
